"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";

interface Slide {
  image: string;
  title: string;
  subtitle: string;
  btnLink: string;
}

const slides: Slide[] = [
  {
    image: "/images/pakistani_students_hero.png",
    title: "Complete Educational Supply Solutions",
    subtitle: "Empowering classrooms across Pakistan with premium stationery, renovation services, and institutional assets.",
    btnLink: "/shop",
  },
  {
    image: "/images/school_furniture_hero.png",
    title: "Premium School & College Furniture",
    subtitle: "High-comfort, ergonomic wooden desks, durable student chairs, and collaborative learning setups.",
    btnLink: "/shop?category=School Furniture",
  },
  {
    image: "/images/pakistani_lab_hero.png",
    title: "Advanced Science Lab Equipment",
    subtitle: "Equipping school laboratories with high-precision microscopes, laboratory apparatus, and glass instruments.",
    btnLink: "/shop?category=Lab Equipment",
  },
  {
    image: "/images/pakistani_teacher_hero.png",
    title: "Modern Classrooms & Professional Coaching",
    subtitle: "Facilitating schools with high-grade teaching visual aids, writing boards, smart setups, and coaching accessories.",
    btnLink: "/shop",
  },
  {
    image: "/images/stationery_close_hero.png",
    title: "Creative Art & High-Quality Stationery",
    subtitle: "Wide collection of premium notebooks, markers, drawing assets, and essential daily study stationery.",
    btnLink: "/shop?category=Stationery Supplies",
  },
  {
    image: "/images/pakistani_computer_hero.png",
    title: "Digital IT & Computer Labs",
    subtitle: "Turnkey setups for high school computer labs, complete workstation desks, clean power solutions, and modern networking.",
    btnLink: "/shop",
  },
];

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrent((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrent((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="relative w-full h-[360px] sm:h-[500px] md:h-[620px] overflow-hidden mt-20 bg-gray-900 group">
      {/* Slides wrapper */}
      <div className="relative w-full h-full">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${
              index === current ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
          >
            {/* Background Image */}
            <div className="absolute inset-0 w-full h-full">
              <Image
                src={slide.image}
                alt={slide.title}
                fill
                priority={index === 0}
                className="object-cover object-center w-full h-full"
                sizes="100vw"
              />
              {/* Dark Overlay gradient for premium readability */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
            </div>

            {/* Content overlay */}
            <div className="absolute inset-0 z-20 flex items-center px-6 sm:px-12 md:px-24">
              <div className="max-w-xl text-left text-white space-y-4 animate-in fade-in slide-in-from-left-4 duration-700">
                <span className="inline-block px-3 py-1 text-xs font-bold tracking-widest text-[#B88E2F] uppercase bg-[#B88E2F]/10 rounded border border-[#B88E2F]/30">
                  Premium Quality
                </span>
                <h1 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight text-white drop-shadow-md">
                  {slide.title}
                </h1>
                <p className="text-xs sm:text-base md:text-lg text-gray-200 leading-relaxed font-light drop-shadow">
                  {slide.subtitle}
                </p>
                <div className="pt-2">
                  <Link href={slide.btnLink}>
                    <button className="bg-[#B88E2F] hover:bg-[#8C6D23] text-white px-6 py-2.5 sm:px-8 sm:py-3.5 rounded-xl text-sm font-semibold transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-[#B88E2F]/30 flex items-center gap-2">
                      Browse Catalog
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-30 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all opacity-0 group-hover:opacity-100"
        aria-label="Previous slide"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-30 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all opacity-0 group-hover:opacity-100"
        aria-label="Next slide"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Navigation Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2.5">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrent(index)}
            className={`transition-all duration-300 rounded-full ${
              index === current
                ? "w-8 h-2 bg-[#B88E2F]"
                : "w-2.5 h-2.5 bg-white/50 hover:bg-white"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
